// signupComponents.js
import React from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import Card from "@material-ui/core/Card";
import Button from "@material-ui/core/Button";
import colors from "./assets/colors";

// don't forward custom props to the DOM
const shouldForwardProp = (prop) => !["maxw", "size"].includes(prop);

export const SignupCard = styled(Card, { shouldForwardProp })(({
  theme,
  maxw,
  size,
}) => {
  // allow semantic sizes too
  const sizeMap = { sm: 560, md: 640, lg: 760, xl: 860 };
  const resolved = maxw || sizeMap[size] || 640;

  return {
    "&&": {
      width: "100%",
      maxWidth: resolved, // <— width controlled here
      padding: theme.spacing(3),
      marginBottom: theme.spacing(4),
      borderRadius: 16,
      border: `2px solid ${colors.mcsdark}`,
      boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
      background: "#fff",
      // keep a nice mobile feel
      [theme.breakpoints.down("sm")]: { maxWidth: "92vw" },
    },
  };
});

/* ② Standard CTA button */
export const CtaButton = styled(Button)(({ theme }) => ({
  "&&": {
    fontFamily: theme.typography.fontFamily, // <- use Ubuntu
    borderRadius: 10,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    padding: theme.spacing(1, 2.25),
    backgroundColor: colors.mcsred,
    color: "#fff",
    "&:hover": { backgroundColor: colors.mcsred },
    "&.Mui-disabled": {
      backgroundColor: "#d8e0e6",
      color: "#738392",
      boxShadow: "none",
    },
  },
}));

/* ③ Step header with top-right back control */
export function StepHeader({
  step = 1,
  eyebrow,
  title,
  backVisible, // optional; by default shows when step > 1
  onBack,
}) {
  const theme = useTheme(); // get Ubuntu from your MUI v5 theme
  const showBack = typeof backVisible === "boolean" ? backVisible : step > 1;

  return (
    <Box mb={2} style={{ fontFamily: theme.typography.fontFamily }}>
      {/* Top row: STEP X (left) + back (right) */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
      >
        <Typography
          style={{
            fontFamily: theme.typography.fontFamily,
            color: colors.mcslight,
            fontWeight: 800,
            letterSpacing: 0.3,
            textTransform: "uppercase",
            fontSize: 14,
          }}
        >
          STEP {step}
        </Typography>

        {showBack && (
          <Box
            onClick={onBack}
            style={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              color: colors.mcsdark,
              userSelect: "none",
              fontFamily: theme.typography.fontFamily,
            }}
          >
            <Tooltip title="Go back">
              <IconButton
                aria-label="Go back"
                style={{ color: colors.mcsdark, padding: 4, marginRight: 4 }}
                size="medium"
              >
                {/* Double chevron to mimic “<<” */}
                <ChevronLeftIcon fontSize="small" />
                <ChevronLeftIcon fontSize="small" style={{ marginLeft: -6 }} />
              </IconButton>
            </Tooltip>
            <Typography
              style={{
                fontFamily: theme.typography.fontFamily,
                fontWeight: 700,
                fontSize: 14,
                textTransform: "none",
                color: colors.mcsdark,
              }}
            >
              Go Back
            </Typography>
          </Box>
        )}
      </Box>

      {/* Eyebrow + Title */}
      {eyebrow && (
        <Typography
          style={{
            fontFamily: theme.typography.fontFamily,
            color: "#6B7A90",
            fontWeight: 800,
            letterSpacing: 0.2,
            textTransform: "uppercase",
            fontSize: 14,
            marginBottom: 8,
          }}
        >
          {eyebrow}
        </Typography>
      )}

      {title && (
        <Typography
          style={{
            fontFamily: theme.typography.fontFamily,
            color: colors.mcsdark,
            fontWeight: 800,
            fontSize: 16,
            marginBottom: 8,
          }}
        >
          {title}
        </Typography>
      )}
    </Box>
  );
}
